import React from 'react';
import PropTypes from 'prop-types';
import { AlertTriangle, AlertCircle, Info, XCircle, RefreshCw } from 'lucide-react';

/**
 * 統一されたエラー表示コンポーネント
 * 既存のエラー表示を置き換える改善版（完全互換）
 */
const ErrorAlert = ({ 
  message,                  // エラーメッセージ（必須、既存と同じ）
  type = 'error',           // エラータイプ: info, warning, error, critical
  onRetry = null,           // 再試行コールバック（既存のclearErrorと同じ）
  retryText = '再試行',     // 再試行ボタンのテキスト
  showRetryButton = true,   // 再試行ボタンの表示制御
  className = '',           // 追加CSSクラス
  autoHide = null,          // 自動非表示時間（ms）
  onClose = null            // 閉じるコールバック
}) => {
  // 自動非表示機能
  React.useEffect(() => {
    if (autoHide && onClose) {
      const timer = setTimeout(() => {
        onClose();
      }, autoHide);
      
      return () => clearTimeout(timer);
    }
  }, [autoHide, onClose]);

  // タイプ別の設定
  const getTypeConfig = () => {
    switch (type) {
      case 'info':
        return {
          icon: Info,
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200',
          textColor: 'text-blue-800',
          iconColor: 'text-blue-600',
          buttonBg: 'bg-blue-500 hover:bg-blue-600'
        };
      case 'warning':
        return {
          icon: AlertTriangle,
          bgColor: 'bg-yellow-50',
          borderColor: 'border-yellow-200',
          textColor: 'text-yellow-800',
          iconColor: 'text-yellow-600',
          buttonBg: 'bg-yellow-500 hover:bg-yellow-600'
        };
      case 'critical':
        return {
          icon: XCircle,
          bgColor: 'bg-red-50',
          borderColor: 'border-red-300',
          textColor: 'text-red-900',
          iconColor: 'text-red-700',
          buttonBg: 'bg-red-600 hover:bg-red-700'
        };
      case 'error':
      default:
        return {
          icon: AlertCircle,
          bgColor: 'bg-red-100',
          borderColor: 'border-red-200',
          textColor: 'text-red-700',
          iconColor: 'text-red-600',
          buttonBg: 'bg-red-500 hover:bg-red-600'
        };
    }
  };

  const config = getTypeConfig();
  const IconComponent = config.icon;

  // メッセージが空の場合は何も表示しない（安全措置）
  if (!message) {
    return null;
  }

  return (
    <div 
      className={`
        ${config.bgColor} 
        border ${config.borderColor} 
        ${config.textColor} 
        p-4 rounded-lg mb-6 
        shadow-sm
        ${className}
      `}
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        {/* アイコン部分 */}
        <div className="flex-shrink-0">
          <IconComponent 
            size={20} 
            className={`${config.iconColor} mt-0.5`}
            aria-hidden="true"
          />
        </div>
        
        {/* メッセージ部分 */}
        <div className="flex-1 min-w-0">
          <div className="whitespace-pre-line break-words">
            {message}
          </div>
          
          {/* 再試行ボタン（既存機能の完全互換） */}
          {showRetryButton && onRetry && (
            <div className="mt-3">
              <button
                onClick={onRetry}
                className={`
                  inline-flex items-center gap-2 px-4 py-2 rounded text-white text-sm font-medium
                  ${config.buttonBg}
                  transition-colors duration-200
                  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500
                `}
                aria-label={`${retryText}ボタン`}
              >
                <RefreshCw size={16} />
                {retryText}
              </button>
            </div>
          )}
        </div>
        
        {/* 閉じるボタン（オプション） */}
        {onClose && (
          <button
            onClick={onClose}
            className={`flex-shrink-0 ${config.iconColor} hover:opacity-75 focus:outline-none`}
            aria-label="エラーメッセージを閉じる"
          >
            <XCircle size={18} />
          </button>
        )}
      </div>
    </div>
  );
};

ErrorAlert.propTypes = {
  message: PropTypes.string.isRequired,           // 既存と同じ（必須）
  type: PropTypes.oneOf(['info', 'warning', 'error', 'critical']),
  onRetry: PropTypes.func,                        // 既存のclearErrorと同じ
  retryText: PropTypes.string,
  showRetryButton: PropTypes.bool,
  className: PropTypes.string,
  autoHide: PropTypes.number,
  onClose: PropTypes.func,
};

export default ErrorAlert; 